<app-page-breadcrumb [breadcrumbs]="breadcrumbs" />
<p-toast />

<div class="space-y-6">
  <!-- Profile header -->
  <div
    class="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]"
  >
    <div class="flex flex-col gap-4 px-6 py-6 sm:flex-row sm:items-center sm:justify-between">
      <div class="flex items-center gap-5">
          <div class="relative">
            @if (userDetails?.profile_image_url) {
              <img
                [src]="userDetails.profile_image_url"
                alt="Profile"
                class="h-24 w-24 rounded-full object-cover ring-4 ring-white dark:ring-gray-900"
              />
            } @else {
              <div
                class="flex h-24 w-24 items-center justify-center rounded-full bg-indigo-100 text-xl font-semibold text-indigo-600 ring-4 ring-white dark:bg-indigo-500/15 dark:text-indigo-300 dark:ring-gray-900"
              >
                {{ initials }}
              </div>
            }
            <button
              type="button"
              (click)="avatarInput.click()"
              aria-label="Change profile picture"
              title="Change profile picture"
              class="absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:text-indigo-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:text-indigo-400"
            >
              <i class="pi pi-camera text-xs"></i>
            </button>
            <input
              #avatarInput
              type="file"
              class="hidden"
              accept="image/*"
              (change)="onImageChange($event)"
            />
          </div>

          <div>
            <h2 class="text-xl font-semibold text-gray-800 dark:text-white/90">
              {{ fullName }}
            </h2>
            <p class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
              <span>{{ userDetails?.role_name || '—' }}</span>
              @if (userDetails?.email) {
                <span class="text-gray-300 dark:text-gray-600">•</span>
                <span class="break-all">{{ userDetails.email }}</span>
              }
            </p>
          </div>
      </div>

      <span
        class="inline-flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold"
        [class]="statusClasses"
      >
        <span class="h-1.5 w-1.5 rounded-full" [class]="statusDotClasses"></span>
        {{ userDetails?.status || 'Unknown' }}
      </span>
    </div>
  </div>

  <!-- Details -->
  <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
    <!-- Personal information -->
    <section
      class="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-2"
    >
      <header class="border-b border-gray-100 px-6 py-4 dark:border-gray-800">
        <h3 class="text-sm font-semibold text-gray-800 dark:text-white/90">Personal Information</h3>
      </header>

      <dl class="grid grid-cols-1 gap-x-8 gap-y-6 px-6 py-6 sm:grid-cols-2">
        <div>
          <dt class="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">First Name</dt>
          <dd class="mt-1 text-sm font-medium break-all text-gray-800 dark:text-white/90">
            {{ userDetails?.first_name || '—' }}
          </dd>
        </div>
        <div>
          <dt class="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Last Name</dt>
          <dd class="mt-1 text-sm font-medium break-all text-gray-800 dark:text-white/90">
            {{ userDetails?.last_name || '—' }}
          </dd>
        </div>
        <div>
          <dt class="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Email Address</dt>
          <dd class="mt-1 text-sm font-medium break-all text-gray-800 dark:text-white/90">
            {{ userDetails?.email || '—' }}
          </dd>
        </div>
        <div>
          <dt class="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Phone Number</dt>
          <dd class="mt-1 text-sm font-medium break-all text-gray-800 dark:text-white/90">
            {{ userDetails?.phone || '—' }}
          </dd>
        </div>
        <div class="sm:col-span-2">
          <dt class="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Organisation</dt>
          <dd class="mt-1 text-sm font-medium break-all text-gray-800 dark:text-white/90">
            {{ userDetails?.organisation_name || '—' }}
          </dd>
        </div>
      </dl>
    </section>

    <!-- Account -->
    <section
      class="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]"
    >
      <header class="border-b border-gray-100 px-6 py-4 dark:border-gray-800">
        <h3 class="text-sm font-semibold text-gray-800 dark:text-white/90">Account</h3>
      </header>

      <ul class="divide-y divide-gray-100 px-6 dark:divide-gray-800">
        <li class="flex items-center justify-between gap-4 py-3.5">
          <span class="text-sm text-gray-500 dark:text-gray-400">Role</span>
          <span class="text-sm font-medium text-right text-gray-800 dark:text-white/90">
            {{ userDetails?.role_name || '—' }}
          </span>
        </li>
        <li class="flex items-center justify-between gap-4 py-3.5">
          <span class="text-sm text-gray-500 dark:text-gray-400">Customer</span>
          <span class="text-sm font-medium text-right text-gray-800 dark:text-white/90">
            {{ userDetails?.customer_name || '—' }}
          </span>
        </li>
        <li class="flex items-center justify-between gap-4 py-3.5">
          <span class="text-sm text-gray-500 dark:text-gray-400">Last Login</span>
          <span class="text-sm font-medium text-right text-gray-800 dark:text-white/90">
            {{ userDetails?.last_login ? (userDetails.last_login | appDate) : '—' }}
          </span>
        </li>
        <li class="flex items-center justify-between gap-4 py-3.5">
          <span class="text-sm text-gray-500 dark:text-gray-400">Member Since</span>
          <span class="text-sm font-medium text-right text-gray-800 dark:text-white/90">
            {{ userDetails?.created_on ? (userDetails.created_on | appDate) : '—' }}
          </span>
        </li>
      </ul>
    </section>
  </div>

  <!-- Preferences -->
  <section
    class="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]"
  >
    <header class="border-b border-gray-100 px-6 py-4 dark:border-gray-800">
      <h3 class="text-sm font-semibold text-gray-800 dark:text-white/90">Preferences</h3>
      <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        Display settings applied across the workspace.
      </p>
    </header>

    <div class="px-6 py-6">
      <div class="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div class="max-w-md">
          <p class="text-sm font-medium text-gray-800 dark:text-white/90">Date &amp; Time Format</p>
          <p class="mt-1 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
            Timestamps across the app are shown in this format, converted to your device timezone.
          </p>
        </div>

        <div class="w-full lg:w-96">
          <app-select
            [groups]="datetimeFormatGroups"
            placeholder="Select a format"
            [value]="selectedDatetimeFormat"
            (valueChange)="onDatetimeFormatChange($event)"
          />
        </div>
      </div>

      <div
        class="mt-5 flex flex-col gap-1 rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.02] sm:flex-row sm:items-baseline sm:gap-4"
      >
        <span class="font-mono text-sm font-semibold text-gray-800 dark:text-white/90">
          {{ previewTimestamp | appDate }}
        </span>
        <span class="text-xs text-gray-500 dark:text-gray-400">
          Preview in your timezone ({{ previewTimestamp | appDate: "zzzz" }}).
        </span>
      </div>
    </div>
  </section>
</div>
